import { Module } from '@user/entities/module';

/**
 * Enum für Containertypen
 * @enum {string}
 */
const ContainerTypeEnum = {
  TEST: 'test',
  FBO: 'fbo',
  TRAINING: 'training',
};

/** Klasse die einen Module Container repräsentiert */
export class ModuleContainer {
  /**
   *
   * @param {object} data - Rohdaten zur Erstellung eines Containers. Geliefert von API loadModuleContainers
   * @param {string} data.name - Name des Containers
   * @param {number} data.id - Eindeutige ID des Containers
   * @param {string} data.description - Beschreibungstext des Containers
   * @param {string} data.iconPath - Pfad zum Icon-Komponent
   * @param {Module[]|Module[][]} data.elements - Die noch nicht abgeschlossenen Elemente des Containers
   * @param {number} data.size - Anzahl der Elemente des Containers
   * @param {ContainerTypeEnum} data.type - Typ des Containers
   * @param {Module[]|Module[][]} data.finishedElements - Die abgeschlossenen Elemente des Containers
   *
   * @returns {ModuleContainer}
   */
  constructor({
    name,
    id,
    description,
    iconPath,
    size,
    elements,
    type,
    finishedElements,
  }) {
    if (!id) {
      throw new TypeError('ModuleContainer must have an id');
    } else {
      this.id = id;
    }

    if (name.length === 0) {
      throw new TypeError('ModuleContainer: name must not be empty');
    } else {
      this.name = name;
    }

    this.description = description;

    if (!iconPath) {
      throw new TypeError('ModuleContainer: must have an icon');
    } else {
      this.icon = () =>
        import(
          /* webpackChunkName: "[request]" */ `@shared/components/Icons/Icons/${iconPath}.vue`
        );
    }
    this.initType(type);
    this.initElements(elements);

    this.activeElement = this.elements[0];
    this.size = size;

    if (finishedElements > this.size) {
      throw new TypeError(
        'ModuleContainer: too many finished elements for size'
      );
    } else {
      switch (this.type) {
        case ContainerTypeEnum.FBO:
          this.finishedElements = [finishedElements];
          break;

        default:
          this.finishedElements = finishedElements;
          break;
      }
    }
    this.finishedAt = this.isFinished
      ? this.finishedElements.reduce((latest, element) => {
          if (latest < element.finishedAt) {
            return element.finishedAt;
          }
          return latest;
        }, '2000-01-01')
      : null;

    // Verhindere zukünftige Modifikation der Attribute vorhandener Properties
    Object.seal(this);
  }

  /** Getter */

  /**
   * Liefert die enthaltenen Module.
   *
   * Das ist vor allem für die Trainings-Container wichtig, weil in diesen alle enthaltenen Module angezeigt und direkt gestartet werden können.
   *
   * @returns {object[]} elements
   * */
  getElements() {
    return this.elements;
  }

  /**
   * Liefert das aktive Element.
   *
   * Der Container funktioniert wie eine Queue. Das Erste Element des elements-Arrays ist immer das aktive Element
   *
   * @returns {number} activeElement
   * */

  getActiveElement() {
    return this.elements[0];
  }

  /**
   * Die geschäzte Bearbeitungszeit des Containers in Minuten.
   * Die Bearbeitungszeit ergibt sich aus den Bearbeitungszeiten der Containerelemente
   *
   * Beispiel:
   * containerDuration = container.duration;
   *
   * @type {number}
   */
  get duration() {
    const timeRequiredReducer = (totalTime, currentElement) =>
      totalTime + currentElement.duration;
    // Wenn FBO Container, muss das bei der Berechnung berücksichtigt werden
    let overallDuration;

    switch (this.type) {
      case ContainerTypeEnum.FBO:
        overallDuration =
          this.elements[0].reduce(timeRequiredReducer, 0) +
            this.finishedElements[0].reduce(timeRequiredReducer, 0) || 0;
        break;

      default:
        overallDuration =
          this.elements.reduce(timeRequiredReducer, 0) +
            this.finishedElements.reduce(timeRequiredReducer, 0) || 0;
        break;
    }

    return overallDuration;
  }

  /**
   * Ob der Container abgeschlossen ist.
   *
   * Ein Container ist abgesclossen, wenn alle Elemente abgesclossen sind.
   *
   * @type {boolean}
   */
  get isFinished() {
    switch (this.type) {
      case ContainerTypeEnum.FBO:
        return this.elements[0].length === 0;

      default:
        return this.elements.length === 0;
    }
  }

  /**
   * Ob der Container ein Trainings-Container ist.
   *
   * @type {boolean}
   */
  get isTraining() {
    return this.type === ContainerTypeEnum.TRAINING;
  }

  /**
   * Der Fortschirtt des Containers als Prozentzahl.
   *
   * @type {number}
   */
  get progressPercentage() {
    return this.finishedElements.length / this.size || -1;
  }

  /** Methoden */

  /**
   * Initialisiert den Containertypen
   *
   * @param {string} containerType
   * @returns {void}
   */
  initType(containerType) {
    const allowedTypes = Object.values(ContainerTypeEnum);
    if (allowedTypes.includes(containerType)) {
      this.type = containerType;
    } else {
      throw new TypeError('invalid container type');
    }
  }

  /**
   * Initialisiert die Container-Elemente
   *
   * Ist der Container ein FBO-Container, werden alle FBO-Elemente als ein Container-Element initialisiert.
   *
   * @param {Array} rawElementArray
   * @returns {void}
   */
  initElements(rawElementArray) {
    rawElementArray.forEach((rawElement) => {
      if (!(rawElement instanceof Module)) {
        this.elements = [];
        throw new TypeError('Elements must be of type Module');
      }
    });
    switch (this.type) {
      case ContainerTypeEnum.FBO:
        this.elements = [rawElementArray];
        break;

      default:
        this.elements = rawElementArray;
        break;
    }
  }

  /**
   * Gehe zum nächsten Container-Schritt
   *
   * Ist der Container  bereits abgeschlossen, setzte das activeElement auf null.
   *
   * @returns {void}
   */
  step() {
    if (this.elements.size === 0) {
      throw new Error('Could not make step: Container already finished.');
    }

    const finishedElement = this.elements.shift();
    this.finishedElements.push(finishedElement);
    this.activeElement = this.elements.size === 0 ? null : this.elements[0];
  }
}
